import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "@/stylesheets/applications/stages.scss";
import { Body, SectionHeader } from "@sprout/typography";
import { Tag } from "@sprout/tag";
import useTranslation from "@hooks/use_translation";
import { stageName } from "@utils/stage_name";
import { sanitizeRichText } from "@components/job_post/rich_text";
import { StageProgressBar } from "./stage_progress";
import type { Stage } from "@/types/application";

type Props = {
  stage: Stage;
  position: number;
  total: number;
};

const CurrentStageCard = ({ stage, position, total }: Props) => {
  const { t } = useTranslation("applications");
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => setExpanded(false), [stage.description]);

  useLayoutEffect(() => {
    const element = descriptionRef.current;
    if (!element || expanded) return;

    setClamped(element.scrollHeight > element.clientHeight);
  }, [stage.description, expanded]);

  return (
    <div className="flex flex-col gap-4 px-6 py-4 rounded-[8px] bg-green-50">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Tag label={t("stage_status.current")} primary />
          <Body as="span" secondary metadata>
            {t("stage_progress", { position, total })}
          </Body>
        </div>
        <SectionHeader as="h3" serif className="!pt-0 !leading-[2.625rem]">
          {stageName(stage, t)}
        </SectionHeader>
        <StageProgressBar position={position} total={total} />
      </div>
      {!!stage.description && (
        <>
          <div
            ref={descriptionRef}
            className={`stage-description stage-description--primary-text ${
              expanded ? "" : "stage-description--clamped"
            }`}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(stage.description) }}
          />
          {clamped && (
            <button
              type="button"
              className="link self-start"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? t("show_less") : t("show_more")}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CurrentStageCard;
